import React, { useMemo } from 'react';
import { Image as RNImage } from 'react-native';
import { Image as ExpoImage } from 'expo-image';
import { resolveListingPhotoCandidates } from '../../utils/listingPhotoResolver';
import { useImageFallback } from '../../hooks/useImageFallback';

/**
 * FallbackImage
 *
 * One reusable image that walks the shared photo fallback chain on load failure:
 * dealer photo(s) -> model AI image -> local placeholder. It works with both image
 * primitives the app uses for listing photos: `expo-image` (default) and the
 * react-native `<Image>` (`primitive="rn"`). DOM `<img>` is only used for static
 * assets, never listing photos, so it is intentionally not supported here.
 *
 * Each rendered image (including each carousel slide) gets its own instance and its
 * own hook state; the caller's photo array is never mutated.
 *
 * See spec: .claude/specs/broken-photo-model-fallback/ (design Component 2).
 *
 * @param {string} [props.dealerPhotoUrl]        primary dealer photo URL
 * @param {string[]|string} [props.dealerPhotoLinks] additional dealer photo URLs
 * @param {string} [props.modelImageUrl]         resolved model AI image URL (never memoized on failure)
 * @param {string} [props.placeholderUrl]        optional URL placeholder (rarely used; prefer placeholderSource)
 * @param {*} [props.placeholderSource]          local placeholder asset, always the last resort
 * @param {'expo'|'rn'} [props.primitive]        which image primitive to render
 */
const DEFAULT_PLACEHOLDER = require('../../../assets/images/car-placeholder.png');

const FallbackImage = ({
  dealerPhotoUrl,
  dealerPhotoLinks,
  modelImageUrl,
  placeholderUrl,
  placeholderSource = DEFAULT_PLACEHOLDER,
  primitive = 'expo',
  style,
  contentFit = 'cover',
  resizeMode,
  transition = 200,
  cachePolicy = 'disk',
  placeholder,
  onError: onErrorProp,
  onLoad: onLoadProp,
  ...rest
}) => {
  const candidates = useMemo(() => {
    const urls = resolveListingPhotoCandidates({
      dealerPhotoUrl,
      dealerPhotoLinks,
      modelImageUrl,
      placeholderUrl,
    });
    const list = urls.map((url) => {
      const isModel = !!modelImageUrl && url === modelImageUrl;
      const isPlaceholderUrl = !!placeholderUrl && url === placeholderUrl;
      // Only dealer URLs are memoized on failure (never model image / placeholder).
      return { source: { uri: url }, url, memoize: !isModel && !isPlaceholderUrl };
    });
    if (placeholderSource != null) {
      // Local asset, always present as the final candidate; never memoized.
      list.push({ source: placeholderSource, url: null, memoize: false });
    }
    return list;
    // dealerPhotoLinks may be a new array each render; callers that pass arrays should
    // memoize, but the hook keys off candidate URLs (content) so correctness holds either way.
  }, [dealerPhotoUrl, dealerPhotoLinks, modelImageUrl, placeholderUrl, placeholderSource]);

  const { source, onError, recyclingKey } = useImageFallback(candidates);

  const handleError = (event) => {
    onError();
    if (onErrorProp) onErrorProp(event);
  };

  if (primitive === 'rn') {
    return (
      <RNImage
        source={source}
        style={style}
        resizeMode={resizeMode || (contentFit === 'contain' ? 'contain' : 'cover')}
        onError={handleError}
        onLoad={onLoadProp}
        {...rest}
      />
    );
  }

  return (
    <ExpoImage
      source={source}
      style={style}
      contentFit={contentFit}
      transition={transition}
      cachePolicy={cachePolicy}
      recyclingKey={recyclingKey}
      placeholder={placeholder}
      onError={handleError}
      onLoad={onLoadProp}
      {...rest}
    />
  );
};

export default FallbackImage;
